import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import RangeSlider from "../RangeSlider/RangeSlider";
import TextField from '@mui/material/TextField';
import { simpleFetch } from "../../../utils/helpsers";

/**
 * Returns a range slider and a dropdown to choose a variable.
 * @param { object } defaultInfo - Information to be displayed by the component on load.
 * @param { number, string } id - An unique identifier for the component.
 * @param { object } year - Value to fetch the variable data in that year.
 * @param { boolean } disabled - Value to control the disabled state.
 * @param { array } variables - The group of variables to be displayed in the dropdown.
 * @param { object } referenceDistrict - The reference district label and the irn value.
 * @param { function } onChange  - A function to be executed on change dropdown value.
 * @returns {JSX.Element}
 */
function VariableFilter({ defaultInfo = null, id, year, disabled, variables, referenceDistrict, onChange }) {
  let initValue = (typeof defaultInfo === "object" && Object.entries(defaultInfo).length > 0) ? defaultInfo : null;

  // Variable data
  const [variableInfo, setVariableInfo] = useState(initValue);
  const variableFilterData = useRef(initValue);

  const updateFilterData = ({ variableData, district, year }) => {
    // Get referent district value related to the selected variable.
    let splitLD = variableData.value_var.split('__');
    let variable = splitLD[0];
    let planNum = splitLD[1];
    let planType = splitLD[2];

    if (splitLD.length <= 1) {
      if (year === 'MCD') { 
        simpleFetch(`${process.env.REACT_APP_API_URL}/mcd_tabloide?select=${variableData.value_var}&irn=eq.${district}`)
        .then(data => {
          variableData = {
            ...variableData,
            'ref_district_val': data[0][variableData.value_var],
          }
          // Get variable ranges based on year selected.
          return simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_range_per_year?_var=${variableData.value_var}&_irn=${district}&_year=${year}`)
        }).then(data => {
          variableData = {
            ...variableData,
            min_range: data[0].min_var,
            max_range: data[0].max_var,
            values: [data[0].min_var, data[0].max_var],
          }
          setVariableInfo(variableData);
          variableFilterData.current = variableData;
          onChange(`var${id}`, variableFilterData.current);
        });
      } else {
        simpleFetch(`${process.env.REACT_APP_API_URL}/hvd_tabloide?select=${variableData.value_var}&irn=eq.${district}&file_year=eq.${year}`)
        .then(data => {
          variableData = {
            ...variableData,
            'ref_district_val': data[0][variableData.value_var],
          }
          // Get variable ranges based on year selected.
          return simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_range_per_year?_var=${variableData.value_var}&_irn=${district}&_year=${year}`)
        }).then(data => {
          variableData = {
            ...variableData,
            min_range: data[0].min_var,
            max_range: data[0].max_var,
            values: [data[0].min_var, data[0].max_var],
          }
          setVariableInfo(variableData);
          variableFilterData.current = variableData;
          onChange(`var${id}`, variableFilterData.current);
        });
      }
    } else {
      if (year === 'MCD') { 
        simpleFetch(`${process.env.REACT_APP_API_URL}/mcd_hb_tabloide?select=${variable}&irn=eq.${district}&plannum=eq.${planNum}&q13_plantype_p7q3=eq.${planType}`)
        .then(data => {
          variableData = {
            ...variableData,
            'ref_district_val': data[0][variable],
          }
          // Get variable ranges based on year selected.
          return simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_range_per_year_health?_var=${variable}&_irn=${district}&_year=${year}&_plannum=${planNum}&_plantype=${planType}`)
        }).then(data => {
          variableData = {
            ...variableData,
            min_range: data[0].min_var,
            max_range: data[0].max_var,
            values: [data[0].min_var, data[0].max_var],
          }
          setVariableInfo(variableData);
          variableFilterData.current = variableData;
          onChange(`var${id}`, variableFilterData.current);
        });
      } else {
        simpleFetch(`${process.env.REACT_APP_API_URL}/health_insurance_readable?select=${variable}&irn=eq.${district}&file_year=eq.${year}&plannum=eq.${planNum}&q13_plantype_p7q3=eq.${planType}`)
        .then(data => {
          variableData = {
            ...variableData,
            'ref_district_val': data[0][variable],
          }
          // Get variable ranges based on year selected.
          return simpleFetch(`${process.env.REACT_APP_API_URL}/rpc/hvd_range_per_year_health?_var=${variable}&_irn=${district}&_year=${year}&_plannum=${planNum}&_plantype=${planType}`)
        }).then(data => {
          variableData = {
            ...variableData,
            min_range: data[0].min_var,
            max_range: data[0].max_var,
            values: [data[0].min_var, data[0].max_var],
          }
          setVariableInfo(variableData);
          variableFilterData.current = variableData;
          onChange(`var${id}`, variableFilterData.current);
        });
      }
    }
  }

  return (
    <>
      <Autocomplete
        id={`var-${id}`}
        options={variables}
        disabled={disabled}
        value={(variableInfo?.varName || variableInfo?.label) || null}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        fullWidth={true}
        onChange={(event, newValue) => {
          if (newValue) {
            setVariableInfo(null);
            updateFilterData({ variableData: newValue, district: referenceDistrict.value, year: year.value })

            // Matomo code.
            window._paq.push([
              "trackEvent",
              "Board CustomGroups",
              `Clicked: #${id}`,
              `Selected: ${newValue.label}`,
            ]);
          }
        }}
        renderInput={(params) => <TextField {...params} label={`#${id}`} />}
        getOptionDisabled={(option) =>
          option.disabled === true
        }
      />

      {
        /** TODO: Remove "variableInfo.varName", "variableInfo?.range", "variableInfo.originalRange" and "variableInfo?.refDistrictVal" variable once the saved group structure is updated */
        (variableInfo?.values || variableInfo?.range) && (
          <RangeSlider
            ranges={[
              variableInfo.originalRange ? variableInfo.originalRange[0] : variableInfo.min_range,
              variableInfo.originalRange ? variableInfo.originalRange[1] : variableInfo.max_range,
            ]}
            values={variableInfo.values || variableInfo?.range}
            id={"y-axis"}
            unit={variableInfo.var_type}
            onChange={(val) => {
              variableFilterData.current['values'] = val;
              onChange(`var${id}`, variableFilterData.current);
            }}
            markData={[
              {
                label: referenceDistrict.label,
                value: variableInfo.ref_district_val || variableInfo.refDistrictVal,
              },
            ]}
            disabled={disabled}
          />
        )}
    </>
  );
}

VariableFilter.propTypes = {
  defaultInfo: PropTypes.object,
  id: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  year: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  variables: PropTypes.array.isRequired,
  referenceDistrict: PropTypes.object.isRequired,
  onChange: PropTypes.func,
}

export default React.memo(VariableFilter);
